import React, { useEffect, useState } from 'react';
import { ConnectWebSocketForScreenV2, DisconnectWebSocket, SetSocketMessageHandler, ClearSocketMessageHandler } from './../../utilities/BL/Zones';

// Importa tus componentes (Juegos y Overlays)
import { GoalScreenV2 } from './Sections/GoalScreenV2';
import { FrontPageRunToGoal } from './Sections/Goal/FrontPageRunToGoal';
import { LuchoFest } from './Sections/LuchoFest';
import { SelfieFan } from './Sections/SelfieFan';
import { InteractiveChat } from './../Overlays/InteractiveChat'; // Ejemplo
import { CarreraContinuaGame, CarreraContinuaCover } from './Sections/CarreraContinua';
import { FightersCover, FightersGame } from './Sections/Fighters';
import { QuickReactionCover, QuickReactionGame } from './Sections/QuickReaction';
import { PlaylistScreen } from './Sections/PlaylistScreen';
import { FrontPageSequence } from './Sections/Sequence/FrontPageSequence';
import { SequenceScreen } from './Sections/SequenceScreen';
import { MemoryCover, MemoryGame } from './Sections/Memory';
import { TugOfWarCover, TugOfWarGame } from './Sections/TugOfWar';


const ScreenV2 = ({ roomId }) => {
    // Definimos qué pantalla somos (Podría venir de la URL, ej: ?screenId=1)
    const screenId = 1; 

    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [toastMessage, setToastMessage] = useState('');

    // ==========================================================================
    // ESTADO GLOBAL DEL LIENZO (El Cerebro)
    // ==========================================================================
    const [screenState, setScreenState] = useState({
        activeMainDynamic: null, // El juego a pantalla completa
        overlays: {}             // Diccionario de widgets flotantes
    });

    const [activeComponent, setActiveComponent] = useState(1);
    
    // Estados para persistir los payloads de cada capa
    const [question, setQuestion] = useState(null);
    const [results, setResults] = useState(null);
    const [lottery, setLottery] = useState(null);
    const [goal, setGoal] = useState(null);
    const [isActiveWomensMode, setIsActiveWomensMode] = useState(false);

    // ==========================================================================
    // ESCUCHADOR DE SIGNALR (SAAS V2)
    // ==========================================================================
    const handleIncomingMessage = (message) => {
        if (!message || !message.type) return;

        console.log(`[ScreenV2] 📥 Mensaje recibido: ${message.type}`, message);

        const msgType = message.type.toLowerCase();
        if (msgType.startsWith('goal.')) {
            const p = message.payload || message.data || {};
            setGoal(prev => ({
                ...(prev || {}),
                ...p,
                theme: p.theme || p.Theme || (p.cfg && (p.cfg.theme || p.cfg.Theme)) || prev?.theme
            }));
        }

        switch (msgType) {
            case 'gendermode': {
                const incomingData = message.data || message.payload || {};
                const isWomens = typeof incomingData === 'boolean'
                    ? incomingData
                    : (incomingData.isActiveWomensMode ?? incomingData.isWomensModeActive ?? false);
                setIsActiveWomensMode(Boolean(isWomens));
                break;
            }
            case 'screenv2.sync':
                // Recibimos la Fotografía exacta desde el Singleton de C#
                if (message.payload) {
                    setScreenState({
                        activeMainDynamic: message.payload.activeMainDynamic,
                        overlays: message.payload.overlays || {}
                    });
                }
                break;

            default:
                break;
        }
    };

    useEffect(() => {
        const connect = async () => {
            await ConnectWebSocketForScreenV2(screenId, setConnectionStatus, showToast);
            SetSocketMessageHandler(handleIncomingMessage);
        };

        connect();

        // Fondo transparente base para OBS
        document.body.classList.add('customBg'); 
        document.body.style.backgroundColor = "transparent";

        return () => {
            const qRoomId = roomId || new URLSearchParams(window.location.search).get('roomId');
            if (!qRoomId) {
                ClearSocketMessageHandler();
                DisconnectWebSocket();
            }
        };
    }, [roomId]);

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(''), 3000);
    };

    // ==========================================================================
    // RENDERIZADO DEL LIENZO (Compositor Broadcast)
    // Usamos position 'absolute' para que todos convivan en el mismo pixel
    // ==========================================================================
    
    // Estilo maestro para que cada capa cubra toda la pantalla sin empujar a las demás
    const layerStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none', // Para que los clics pasen a través de las capas transparentes
        overflow: 'hidden'
    };

    return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh' }}>
            
            {/* -----------------------------------------------------------
                NIVEL 10: WIDGETS DE FONDO / FIJOS 
                (Ej. El Marcador del partido, un cintillo de noticias)
            ----------------------------------------------------------- */}
            <div style={{ ...layerStyle, zIndex: 10 }}>
                {/* {screenState.overlays['Scoreboard']?.isVisible && <Scoreboard />} */}
            </div>


            {/* -----------------------------------------------------------
                NIVEL 20: OVERLAYS DE INTERACCIÓN 
                (Cosas que flotan con transparencia, ej. Selfies, Mensajes)
            ----------------------------------------------------------- */}
            <div style={{ ...layerStyle, zIndex: 20 }}>
                {/* Asumiendo que tu componente SelfieFan tiene fondo transparente en su CSS */}
                {screenState.overlays['Selfies']?.isVisible && (
                    <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
                        <SelfieFan />
                    </div>
                )}
                
                {screenState.overlays['Chat']?.isVisible && (
                    <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
                        <InteractiveChat />
                    </div>
                )}
            </div>


            {/* -----------------------------------------------------------
                NIVEL 50: DINÁMICAS PRINCIPALES (FULL SCREEN)
                (Juegos pesados que tapan todo el estadio)
            ----------------------------------------------------------- */}
            <div style={{ ...layerStyle, zIndex: 50, pointerEvents: 'auto' }}>
                
                {/* Si el backend dice "RunToGoal", lo inyectamos */}
                {screenState.activeMainDynamic === 'RunToGoal' && (
                    <GoalScreenV2 currentMatchState={goal} />
                )}

                {/* Si el backend dice "RunToGoalFrontPage", proyectamos la portada */}
                {(screenState.activeMainDynamic === 'RunToGoalFrontPage' || screenState.activeMainDynamic === 'GoalFrontPage') && (
                    <FrontPageRunToGoal currentMatchState={goal} />
                )}

                {/* Si el backend dice "LuchoFest", lo inyectamos */}
                {screenState.activeMainDynamic === 'LuchoFest' && (
                    <LuchoFest /> 
                )}

                {/* Si el backend dice "CarreraContinua", proyectamos el juego de atletismo */}
                {screenState.activeMainDynamic === 'CarreraContinua' && (
                    <CarreraContinuaGame roomId={roomId} />
                )}

                {/* Si el backend dice "CarreraContinuaCover", proyectamos la portada Full HD */}
                {screenState.activeMainDynamic === 'CarreraContinuaCover' && (
                    <CarreraContinuaCover roomId={roomId} />
                )}

                {/* Si el backend dice "Fighters", proyectamos el combate a 60 FPS */}
                {screenState.activeMainDynamic === 'Fighters' && (
                    <FightersGame roomId={roomId} />
                )}

                {/* Si el backend dice "FightersCover", proyectamos la portada */}
                {screenState.activeMainDynamic === 'FightersCover' && (
                    <FightersCover roomId={roomId} />
                )}

                {/* Si el backend dice "QuickReaction", proyectamos el combate 1 vs 1 */}
                {(screenState.activeMainDynamic === 'QuickReaction' || screenState.activeMainDynamic === 'QuickReactionGame') && (
                    <QuickReactionGame roomId={roomId} />
                )}

                {/* Si el backend dice "QuickReactionCover", proyectamos la portada Full HD */}
                {screenState.activeMainDynamic === 'QuickReactionCover' && (
                    <QuickReactionCover roomId={roomId} />
                )}

                {/* Si el backend dice "Playlist" o "ArmaLaPlaylist", proyectamos la pantalla de estadio */}
                {(screenState.activeMainDynamic === 'Playlist' || screenState.activeMainDynamic === 'ArmaLaPlaylist') && (
                    <PlaylistScreen roomId={roomId} />
                )}

                {/* Si el backend dice "SequenceScreen" o "Sequence", proyectamos el tablero Simón */}
                {(screenState.activeMainDynamic === 'Sequence' || screenState.activeMainDynamic === 'SequenceScreen' || screenState.activeMainDynamic === 'Secuencia') && (
                    <SequenceScreen screenId={screenId} isStandalone={true} roomId={roomId} />
                )}

                {/* Si el backend dice "FrontPageSequence" o "SequenceCover", proyectamos la portada Full HD */}
                {(screenState.activeMainDynamic === 'SequenceCover' || screenState.activeMainDynamic === 'FrontPageSequence' || screenState.activeMainDynamic === 'SequenceFrontPage') && (
                    <FrontPageSequence />
                )}

                {/* Si el backend dice "Memory" o "MemoryGame", proyectamos la pantalla de estadio 1 vs 1 */}
                {(screenState.activeMainDynamic === 'Memory' || screenState.activeMainDynamic === 'MemoryGame' || screenState.activeMainDynamic === 'Memorama' || screenState.activeMainDynamic === 'MemoryScreen') && (
                    <MemoryGame roomId={roomId} />
                )}

                {/* Si el backend dice "MemoryCover" o "FrontPageMemory", proyectamos la portada Full HD */}
                {(screenState.activeMainDynamic === 'MemoryCover' || screenState.activeMainDynamic === 'FrontPageMemory' || screenState.activeMainDynamic === 'MemoramaCover') && (
                    <MemoryCover roomId={roomId} />
                )}

                {/* Si el backend dice "TugOfWar" o "EstiraYAfloja", proyectamos la pantalla de juego de estadio */}
                {(screenState.activeMainDynamic === 'TugOfWar' || screenState.activeMainDynamic === 'TugOfWarGame' || screenState.activeMainDynamic === 'EstiraYAfloja' || screenState.activeMainDynamic === 'EstiraYAflojaGame') && (
                    <TugOfWarGame roomId={roomId} />
                )}

                {/* Si el backend dice "TugOfWarCover" o "EstiraYAflojaCover", proyectamos la portada Full HD */}
                {(screenState.activeMainDynamic === 'TugOfWarCover' || screenState.activeMainDynamic === 'EstiraYAflojaCover' || screenState.activeMainDynamic === 'FrontPageTugOfWar') && (
                    <TugOfWarCover roomId={roomId} />
                )}

            </div>


            {/* MENSAJES DE SISTEMA */}
            {toastMessage && (
                <div className="toast-container position-fixed bottom-0 start-50 translate-middle-x mb-3" style={{ zIndex: 9999 }}>
                    <div className="toast show bg-dark text-white px-3 py-2 shadow rounded border-light">
                        {toastMessage}
                    </div>
                </div>
            )}
        </div>
    );
};

export { ScreenV2 };